import { lighten } from "@mui/material";
import { GridColDef } from "@mui/x-data-grid";
import * as React from "react";
import { useNavigate } from "react-router-dom";
import { ButtonGridCol } from "../components/CrudTable/columns/ButtonGridCol";
import { DateGridCol } from "../components/CrudTable/columns/DateTimeGridCol";
import { MutableGridCol } from "../components/CrudTable/columns/MutableGridCol";
import { SelectGridCol } from "../components/CrudTable/columns/SelectGridCol";
import { FullFeaturedCrudGrid } from "../components/CrudTable/FullFeaturedCrudGrid";
import { CustomSpinner } from "../components/CustomSpinner";
import { PageLayout } from "../components/PageLayout";
import {
  Match,
  useCreateMatch,
  useDeleteMatch,
  useMatches,
  useUpdateMatch,
} from "../requests/matches";
import { useMatchTypes } from "../requests/matchTypes";
import { useGetTeam, useTeams } from "../requests/teams";
import { UserPermission } from "../requests/users";
import { LARGE_COLUMN, XL_COLUMN, XXL_COLUMN } from "../utils/columnSizes";
import { ColorPalette } from "../utils/constants";
import { getDisplayDate } from "../utils/getDisplayDate";
import { isLargeRandomInt } from "../utils/getLargeRandomInt";
import { getMatchName } from "../utils/getMatchName";
import { isValidVideoUrl } from "../utils/isValidVideoUrl";

const isValidMatch = (match?: Match): boolean =>
  !!(
    match &&
    match.home_team_id &&
    match.away_team_id &&
    match.start_time &&
    isValidVideoUrl(match.video_url)
  );

export const Matches = () => {
  const teams = useTeams();
  const getTeam = useGetTeam();
  const matches = useMatches();
  const matchTypes = useMatchTypes();
  const createMatch = useCreateMatch().mutateAsync;
  const updateMatch = useUpdateMatch().mutate;
  const deleteMatch = useDeleteMatch().mutate;

  const navigate = useNavigate();

  if (!matches || !matchTypes || !teams) {
    return <CustomSpinner />;
  }

  const columns: GridColDef[] = [
    new ButtonGridCol(
      "load",
      "Load",
      LARGE_COLUMN,
      (params) => navigate(`/tracker/${params.id}`),
      lighten(ColorPalette.blue, 0.25),
      (params) => isLargeRandomInt(params.row.id)
    ),
    new SelectGridCol<Match>(
      "home_team_id",
      "Home Team",
      XL_COLUMN,
      teams
        .sort((a, b) => a.name.localeCompare(b.name))
        .map((team) => ({ value: team.id, label: team.name })),
      (params) => getTeam(params.value)?.name ?? ""
    ),
    new SelectGridCol<Match>(
      "away_team_id",
      "Away Team",
      XL_COLUMN,
      teams
        .sort((a, b) => a.name.localeCompare(b.name))
        .map((team) => ({ value: team.id, label: team.name })),
      (params) => getTeam(params.value)?.name ?? ""
    ),
    new SelectGridCol<Match>(
      "match_type_id",
      "Match Type",
      LARGE_COLUMN,
      matchTypes
        .sort((a, b) => a.name.localeCompare(b.name))
        .map((matchType) => ({
          value: matchType.id, // numeric representation
          label: matchType.name, // string representation
        })),
      (params) => matchTypes.find((matchType) => matchType.id === params.value)?.name ?? ""
    ),
    new DateGridCol("start_time", "Date", XL_COLUMN),
    new MutableGridCol("location", "Location", XXL_COLUMN),
    new MutableGridCol("video_url", "YouTube URL", XXL_COLUMN),
  ];

  const getNewRow = (id: number): Match => ({
    id,
    home_team_id: "",
    away_team_id: "",
    start_time: new Date(),
    video_url: "",
    location: "",
    match_type_id: 1,
  });

  const getDeleteDialog = (match: Match) =>
    `Delete ${getMatchName(
      getTeam(match?.home_team_id)?.name,
      getTeam(match?.away_team_id)?.name,
      getDisplayDate(match?.start_time)
    )}?`;

  return (
    <PageLayout
      title="Matches"
      subtitle="Select a match to start tracking events."
      maxWidth="xl"
      showPaper={false}
    >
      <FullFeaturedCrudGrid
        crudRequests={{
          isStoredAsBlob: false,
          useCollection: useMatches,
          postSingleton: (match: Match) => createMatch(match),
          putSingleton: (match: Match) => updateMatch(match),
          deleteSingleton: (id: number) => deleteMatch(id),
        }}
        validators={{
          isValidCreateUpdate: isValidMatch,
        }}
        columns={columns}
        getNewRow={getNewRow}
        getDeleteDialog={getDeleteDialog}
        itemName="Match"
        lockKey="max_matches"
        initialSortModel={[{ field: "start_time", sort: "desc" }]}
        fieldToFocus="home_team_id"
        minUserPermission={UserPermission.track}
      />
    </PageLayout>
  );
};
